import { useState, useEffect } from 'react';
import { UserMembership, MEMBERSHIP_TIERS, VideoAnalysisUsage } from '../types/membership';

export const useMembership = (userId: string) => {
  const [membership, setMembership] = useState<UserMembership | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMembership();
  }, [userId]);

  const loadMembership = () => {
    try {
      const stored = localStorage.getItem(`membership_${userId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        setMembership({
          ...parsed,
          startDate: new Date(parsed.startDate),
          endDate: new Date(parsed.endDate)
        });
      } else {
        setMembership(null);
      }
    } catch (error) {
      console.error('Error loading membership:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveMembership = (newMembership: UserMembership) => {
    try {
      localStorage.setItem(`membership_${userId}`, JSON.stringify(newMembership));
      setMembership(newMembership);
    } catch (error) {
      console.error('Error saving membership:', error);
    }
  };

  const upgradeMembership = (tierId: string, sport?: 'pickleball' | 'tennis' | 'golf') => {
    const tier = MEMBERSHIP_TIERS.find(t => t.id === tierId);
    if (!tier) return;

    const newMembership: UserMembership = {
      userId,
      tier: tierId,
      sport,
      startDate: new Date(),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
      videosUsedThisMonth: 0,
      videosIncluded: tier.includedVideos,
      totalVideosPurchased: 0,
      isActive: true
    };

    saveMembership(newMembership);
  };

  const purchaseVideoAnalysis = () => {
    if (!membership) return false;

    const tier = MEMBERSHIP_TIERS.find(t => t.id === membership.tier);
    if (!tier) return false;

    // Check if they have included videos left
    if (membership.videosUsedThisMonth < membership.videosIncluded) {
      const updatedMembership = {
        ...membership,
        videosUsedThisMonth: membership.videosUsedThisMonth + 1
      };
      saveMembership(updatedMembership);
      return true;
    }

    // They need to purchase additional videos
    return false; // This would trigger payment flow
  };

  const canAnalyzeVideo = (): { canAnalyze: boolean; reason?: string; cost?: number } => {
    if (!membership || !membership.isActive) {
      return { canAnalyze: false, reason: 'No active membership' };
    }

    const tier = MEMBERSHIP_TIERS.find(t => t.id === membership.tier);
    if (!tier) {
      return { canAnalyze: false, reason: 'Invalid membership tier' };
    }

    // Check if they have included videos left
    if (membership.videosUsedThisMonth < membership.videosIncluded) {
      return { canAnalyze: true };
    }

    // They need to purchase additional videos
    return { 
      canAnalyze: false, 
      reason: 'Monthly limit reached', 
      cost: tier.additionalVideoPrice 
    };
  };

  const getRemainingVideos = (): number => {
    if (!membership) return 0;
    return Math.max(0, membership.videosIncluded - membership.videosUsedThisMonth);
  };

  const resetMonthlyUsage = () => {
    if (!membership) return;
    
    const updatedMembership = {
      ...membership,
      videosUsedThisMonth: 0
    };
    saveMembership(updatedMembership);
  };

  return {
    membership,
    loading,
    upgradeMembership,
    loadMembership,
    purchaseVideoAnalysis,
    canAnalyzeVideo,
    getRemainingVideos,
    resetMonthlyUsage
  };
};












